/**
 * Mutation of a piece upload (fiches 22975 / 22976) — `uploadPiece` with the
 * VEOS environment and the logged-in person (`erp.person.id`, the `user` of
 * call 1) of the extranet auth store.
 *
 * On success it invalidates every view of the pending pieces:
 * - the pieces list of the contract / claim (its « Pièces (n) » tab badge is
 *   derived from it);
 * - the `WS2_EA_CTR_PMQ` counter — the header bell and the Accueil banner;
 * - the global « Pièces à fournir » list (`WS2_EA_TAB_PMQ`).
 * The invalidations are fired, not awaited: the zone resets as soon as the
 * two calls succeeded.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { contractQueryKeys } from '../api/contractsService';
import { uploadPiece, type UploadPieceResult } from '../api/pieceUploadService';
import { pendingPiecesQueryKey } from '../api/piecesService';
import { useExtranetAuthStore } from '../auth/store';
import { claimQueryKey } from '../components/claims/useClaimQueries';
import type { PieceUploadTarget } from '../components/shared/pieceItems';
import { pendingPiecesCountQueryKey } from './usePendingPiecesCount';

/** Variables of one upload. */
export interface PieceUploadVariables {
  target: PieceUploadTarget;
  file: File;
}

/** The query keys an upload of `target` invalidates. */
export function pieceUploadInvalidationKeys(
  target: PieceUploadTarget,
  registrySlug: string | null,
  sessionId: number | null,
): ReadonlyArray<readonly unknown[]> {
  const parentId = String(target.parentId);
  return [
    target.origin === 'contrat'
      ? contractQueryKeys.detail(registrySlug, parentId, 'pieces')
      : claimQueryKey(registrySlug, sessionId, parentId, 'pieces'),
    pendingPiecesCountQueryKey(registrySlug, sessionId),
    pendingPiecesQueryKey(registrySlug, sessionId),
  ];
}

export function usePieceUpload(): UseMutationResult<
  UploadPieceResult,
  Error,
  PieceUploadVariables
> {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  const userId = useExtranetAuthStore((s) => s.person?.id ?? null);
  const queryClient = useQueryClient();

  return useMutation<UploadPieceResult, Error, PieceUploadVariables>({
    mutationFn: ({ target, file }) => uploadPiece({ registrySlug, target, file, userId }),
    onSuccess: (_result, { target }) => {
      for (const queryKey of pieceUploadInvalidationKeys(target, registrySlug, sessionId)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
